import React from "react";
import OrderNotes from "assets/images/brands/OrderNotes.png";
import type { SalesOrderDetailsTimelineItem as TimelineItem } from "components/SalesOrderDetails/types";
import { getMarkerColors } from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";
import { SALES_ORDER_DETAILS_ORDER_LOG_CSS } from "components/SalesOrderDetails/salesOrderDetailsEmbeddedCss";

export type SalesOrderDetailsOrderLogProps = {
  mergedOrderLogItems: TimelineItem[];
  colors: string[];
  parseDate: (d: any) => string;
  order: any;
};

function capitalizeFirst(str: string | undefined | null): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Order Log card: timeline list + embedded styles.
 */
export function SalesOrderDetailsOrderLog(props: SalesOrderDetailsOrderLogProps) {
  const { mergedOrderLogItems, colors, parseDate, order } = props;
  const allOrderLogItems = React.useMemo(() => {
    const poLogItems: TimelineItem[] = (order?.po_number_log ?? []).map((entry: any) => ({
      type: "po_number_update",
      subtitle: (
        <>
          PO Number Updated:
          {" "}
          <span className="fw-semibold">{String(entry?.po_number_log ?? "").trim() || "—"}</span>
        </>
      ),
      at: entry?.sent_at,
      by: entry?.updated_by?.name || "",
    }));

    const combined = [...mergedOrderLogItems, ...poLogItems];
    const toMs = (v: any) => {
      const t = new Date(v as any).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return combined.sort((a, b) => toMs(b.at) - toMs(a.at));
  }, [mergedOrderLogItems, order?.po_number_log]);

  return (
    <div className="col-lg-12  mb-5">
      <div className="card box-shadow-datatables shadow-border-2 mb-5">
        <div className="ms-3 py-3">
          <h6 className="mb-0 d-flex align-items-center order-tittle">
            <span
              aria-hidden="true"
              className="image-icons"
              style={{
                backgroundColor: "#3b82f6",
                WebkitMaskImage: `url(${OrderNotes})`,
                maskImage: `url(${OrderNotes})`,
              }}
            />
            <p className="mb-0 ms-2">Order Log</p>
          </h6>
        </div>

        <div className="card-body p-0">
          <div style={{ maxHeight: 450, overflowY: "auto" }}>
            <ul className="ol list-unstyled mb-0">
              {allOrderLogItems.map((item, idx) => {
                const isLast = idx === allOrderLogItems.length - 1;
                const statusText =
                  typeof item.subtitle === "string"
                    ? item.subtitle
                    : (item as any)?.status ?? (item as any)?.type ?? "";
                const c = getMarkerColors(statusText, idx, colors);

                return (
                  <li
                    key={`${(item as any).type || "row"}-${idx}`}
                    className={`ol-row d-flex align-items-start ${isLast ? "last" : ""}`}
                  >
                    <div className="ol-marker">
                      <span
                        className="ol-bubble order-tittle "
                        style={
                          {
                            "--bubble": c.bubble,
                            "--halo": c.halo,
                            "--dot": c.dot,
                          } as React.CSSProperties
                        }
                      >
                        <span className="ol-dot" />
                      </span>
                      {!isLast && <span className="ol-connector" />}
                    </div>

                    <div className="ms-3 flex-grow-1">
                      <div className="ol-content d-flex align-items-center flex-wrap gap-2">
                        {typeof item.subtitle === "string" ? (
                          <span className="order-tittle ">{item.subtitle}</span>
                        ) : (
                          item.subtitle
                        )}
                      </div>
                    </div>

                    <div className="ol-time ms-auto order-tittle ">
                      {(() => {
                        if (item.by) {
                          const name =
                            typeof item.by === "object" && item.by?.name
                              ? capitalizeFirst(item.by.name)
                              : typeof item.by === "string"
                                ? capitalizeFirst(item.by)
                                : String(item.by);
                          return `By ${name}, `;
                        }
                        if (order?.signage_company_name) {
                          return `By ${capitalizeFirst(order.signage_company_name)}, `;
                        }
                        return "By SYSTEM, ";
                      })()}
                      {parseDate(item.at)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <style>{SALES_ORDER_DETAILS_ORDER_LOG_CSS}</style>
    </div>
  );
}
